// @ts-ignore
import { MouseEvent, useEffect, useState } from "react";

import { ProductKeys } from "@atlassiansox/asc-stargate";

import { useProductTemplateContext } from "@/components/product-template-context-provider";
import { useSiteSelectorContext } from "@/components/site-selector-modal/index.ts";
import { getTemplateAction } from "@/components/site-selector-modal/template-action-utils";
import { useSitesWithCurrentProduct } from "@/components/user-context/helpers/cohorts";
import {
    Button,
    ButtonProps
} from "@/libraries/design/components/button/src/button.tsx";
import {
    useLocale,
    getLocalizedHref,
    useLocalizedHref
} from "@/libraries/web-framework/index.ts";

export interface UniformTemplateModalButtonProps {
    text: string;
    productKey: string;
    templateName: string;
    pluginModuleKey?: string;
    href?: string;
}

const UniformTemplateModalButton = (props: UniformTemplateModalButtonProps) => {
    const { text, productKey, templateName, pluginModuleKey, href } = props;

    const { openModalForTemplate } = useSiteSelectorContext();
    const { setTemplate } = useProductTemplateContext();
    // AIS = Atlassian Identity Service - provides user's existing Atlassian sites
    const aisSites = useSitesWithCurrentProduct();
    const locale = useLocale();
    const localizedSignupHref = useLocalizedHref("/try/cloud/signup");

    const [buttonProps, setButtonProps] = useState<ButtonProps>({
        href: getDefaultHref({
            productKey,
            locale
        })
    });
    const fullStargateProductKey =
        productKey === "jira-service-management"
            ? "jira-servicedesk.ondemand"
            : `${productKey}.ondemand`;

    useEffect(() => {
        if (pluginModuleKey && productKey) {
            const templateData = {
                templateId: pluginModuleKey,
                templateName,
                productKey: fullStargateProductKey as ProductKeys
            };

            setTemplate(templateData);

            // Get site URLs for the current product
            const siteUrls = aisSites?.map((site) => site.url) ?? [];

            // Determine action based on user's sites
            const action = getTemplateAction({
                templateData,
                siteUrls,
                localizedSignupHref
            });

            if (action.type === "modal") {
                setButtonProps({
                    onClick: (event: MouseEvent<HTMLButtonElement>) => {
                        event.stopPropagation();
                        event.preventDefault();
                        openModalForTemplate(templateData);
                    }
                });
            } else {
                setButtonProps({
                    href: action.href
                });
            }
        } else if (href) {
            setButtonProps({
                href: getLocalizedHref({ href, locale })
            });
        }

        return () => {
            setTemplate(null);
        };
    }, [
        openModalForTemplate,
        templateName,
        pluginModuleKey,
        fullStargateProductKey,
        href,
        productKey,
        locale,
        aisSites,
        localizedSignupHref,
        setTemplate
    ]);

    return <Button {...buttonProps}>{text}</Button>;
};

interface GetDefaultHrefArgs {
    productKey: string;
    locale: string;
}
function getDefaultHref(args: GetDefaultHrefArgs) {
    const { locale, productKey } = args;

    if (productKey === "jira-software") {
        return getLocalizedHref({
            href: "/try/cloud/signup?bundle=jira&edition=free",
            locale
        });
    }
    if (productKey === "confluence") {
        return getLocalizedHref({
            href: "/try/cloud/signup?bundle=confluence&edition=free",
            locale
        });
    }
    if (productKey === "jira-service-management") {
        return getLocalizedHref({
            href: "/try/cloud/signup?bundle=jira-service-management&edition=free",
            locale
        });
    }

    return "";
}

export default UniformTemplateModalButton;
